import { useEffect } from "react";
import { Button, Table } from "reactstrap";
import { usePartRefreshQueue } from "../../context/PartRefreshQueueContext";
import "./partRefreshDock.css";

const statusLabel = (item) => {
  if (item.status === "success") {
    return "Success";
  }
  if (item.status === "cancelled") {
    return "Cancelled";
  }
  if (item.status === "failed") {
    return item.message || "Failed to fetch";
  }
  if (item.status === "syncing") {
    return item.message || "Refreshing…";
  }
  if (item.status === "pending") {
    return item.message || "Queued";
  }
  return "Queued";
};

const canCancel = (item) =>
  item.status === "pending" || item.status === "syncing";

const PartRefreshQueueDock = () => {
  const {
    items,
    panelOpen,
    setPanelOpen,
    isRunning,
    retryFailed,
    cancelJob,
    cancelAll,
    clearFinished,
  } = usePartRefreshQueue();

  useEffect(() => {
    const visible = items.length > 0;
    document.body.classList.toggle("has-part-refresh-dock", visible);
    document.body.classList.toggle(
      "has-part-refresh-dock-open",
      visible && panelOpen,
    );
    return () => {
      document.body.classList.remove("has-part-refresh-dock");
      document.body.classList.remove("has-part-refresh-dock-open");
    };
  }, [items.length, panelOpen]);

  if (!items.length) {
    return null;
  }

  const pendingCount = items.filter(
    (item) => item.status === "pending" || item.status === "syncing",
  ).length;
  const successCount = items.filter((item) => item.status === "success").length;
  const failedCount = items.filter((item) => item.status === "failed").length;
  const activeItems = items.slice(-12).reverse();

  return (
    <div className="part-refresh-dock">
      <button
        type="button"
        className="part-refresh-dock__toggle"
        onClick={() => setPanelOpen(!panelOpen)}
      >
        <i className={`fa fa-refresh${isRunning ? " fa-spin" : ""}`} />
        {isRunning
          ? ` Refreshing DigiKey · ${pendingCount} remaining`
          : ` DigiKey refresh · ${successCount} done${
              failedCount ? ` · ${failedCount} failed` : ""
            }`}
      </button>
      {panelOpen ? (
        <div className="part-refresh-dock__panel">
          <div className="part-refresh-dock__header">
            <strong>DigiKey refresh queue</strong>
            <span className="text-muted small">
              Continues if you change pages
            </span>
          </div>
          <div className="part-refresh-dock__body">
            <Table className="table-sm mb-0 part-refresh-dock__table">
              <thead>
                <tr>
                  <th className="part-refresh-dock__col-mpn">MPN</th>
                  <th className="part-refresh-dock__col-progress">Progress</th>
                  <th className="part-refresh-dock__col-status">Status</th>
                  <th className="part-refresh-dock__col-cancel" />
                </tr>
              </thead>
              <tbody>
                {activeItems.map((item) => (
                  <tr key={item.id} className={`is-${item.status}`}>
                    <td className="part-refresh-dock__col-mpn">
                      <strong className="part-refresh-dock__mpn-text">
                        {item.partNumber}
                      </strong>
                      {item.manufacturer ? (
                        <div className="text-muted small part-refresh-dock__mpn-text">
                          {item.manufacturer}
                        </div>
                      ) : null}
                    </td>
                    <td className="part-refresh-dock__col-progress">
                      <div className="part-refresh-progress">
                        <div
                          className={`part-refresh-progress__bar is-${item.status}`}
                          style={{ width: `${item.displayProgress ?? item.progress ?? 0}%` }}
                        />
                      </div>
                      <div className="small text-muted">
                        {item.displayProgress ?? item.progress ?? 0}%
                      </div>
                    </td>
                    <td className="part-refresh-dock__col-status">
                      <span className={`part-refresh-status is-${item.status}`}>
                        {statusLabel(item)}
                      </span>
                    </td>
                    <td className="part-refresh-dock__col-cancel">
                      {canCancel(item) ? (
                        <button
                          type="button"
                          className="part-refresh-cancel-btn"
                          title="Cancel"
                          aria-label="Cancel this refresh"
                          onClick={() => cancelJob(item)}
                        >
                          <i className="fa fa-times" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="part-refresh-dock__footer">
            <Button
              type="button"
              color="secondary"
              size="sm"
              onClick={() => setPanelOpen(false)}
            >
              Hide
            </Button>
            {pendingCount > 0 ? (
              <Button
                type="button"
                color="danger"
                size="sm"
                outline
                onClick={cancelAll}
              >
                Cancel all
              </Button>
            ) : null}
            {failedCount > 0 ? (
              <Button
                type="button"
                color="primary"
                size="sm"
                disabled={isRunning}
                onClick={retryFailed}
              >
                Retry failed
              </Button>
            ) : null}
            {!isRunning ? (
              <Button
                type="button"
                color="link"
                size="sm"
                onClick={clearFinished}
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PartRefreshQueueDock;
